import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AlertFeed = ({ alerts }) => {
  const [isVisible, setIsVisible] = useState(true);

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-error/10',
          border: 'border-error',
          text: 'text-error',
          icon: 'AlertCircle'
        };
      case 'warning':
        return {
          bg: 'bg-warning/10',
          border: 'border-warning',
          text: 'text-warning',
          icon: 'AlertTriangle'
        };
      case 'info':
        return {
          bg: 'bg-primary/10',
          border: 'border-primary',
          text: 'text-primary',
          icon: 'Info'
        };
      default:
        return {
          bg: 'bg-muted',
          border: 'border-border',
          text: 'text-muted-foreground',
          icon: 'Bell'
        };
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffMs = now - alertTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return alertTime?.toLocaleDateString();
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Bell" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">
            Real-Time Alerts
          </h3>
          <div className="flex items-center gap-1.5 px-2 py-1 bg-success/10 rounded-full ml-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            <span className="text-xs font-medium text-success">Live</span>
          </div>
        </div>
        <button
          onClick={() => setIsVisible(!isVisible)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground bg-muted hover:bg-muted/80 rounded-md transition-base"
        >
          <Icon name={isVisible ? "EyeOff" : "Eye"} size={16} />
          {isVisible ? 'Hide' : 'Show'}
        </button>
      </div>
      
      {isVisible && (
        <div className="p-4">
          {alerts?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-3">
                <Icon name="CheckCircle" size={32} color="var(--color-success)" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">
                All Systems Normal
              </p>
              <p className="text-xs text-muted-foreground">
                No alerts or anomalies detected
              </p>
            </div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {alerts?.map((alert) => {
                const styles = getSeverityStyles(alert?.severity);
                return (
                  <div
                    key={alert?.id}
                    className={`${styles?.bg} border ${styles?.border} rounded-lg p-3 transition-base hover:shadow-md flex-shrink-0 w-[320px]`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${styles?.bg} flex items-center justify-center`}>
                        <Icon name={styles?.icon} size={16} color={`var(--color-${alert?.severity === 'critical' ? 'error' : alert?.severity === 'warning' ? 'warning' : 'primary'})`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className={`text-sm font-medium ${styles?.text}`}>
                            {alert?.title}
                          </h4>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatTimestamp(alert?.timestamp)}
                          </span>
                        </div>
                        
                        <p className="text-xs text-muted-foreground mb-2">
                          {alert?.message}
                        </p>

                        {alert?.metrics && (
                          <div className="flex flex-wrap gap-2">
                            {alert?.metrics?.map((metric, index) => (
                              <div 
                                key={index}
                                className="flex items-center gap-1 px-2 py-1 bg-background rounded text-xs"
                              >
                                <span className="text-muted-foreground">{metric?.label}:</span>
                                <span className="font-medium text-foreground">{metric?.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertFeed;